// ---------------------------------------------------------------------------
// about.content.ts — Translated content for AboutPage (all 8 locales)
// ---------------------------------------------------------------------------
import type { AboutContent, AboutContentMap } from './about.types';

const en: AboutContent = {
  header: { title: 'About HotelStack', description: 'Empowering the future of hospitality management.' },
  mission: { title: 'Our Mission', description: 'To empower hotels of every size with an enterprise-grade management platform that streamlines operations, maximizes revenue, and delivers exceptional guest experiences -- all from a single, unified dashboard.' },
  vision: { title: 'Our Vision', description: 'To become the global standard in cloud-based hospitality technology, enabling seamless multi-property management and setting new benchmarks for operational excellence, data-driven decision making, and guest satisfaction.' },
  stats: [
    { label: 'Hotels Onboarded', value: '2,500+' },
    { label: 'Bookings Processed', value: '250K+' },
    { label: 'Countries Served', value: '40+' },
    { label: 'Active Users', value: '65,000+' },
  ],
  valuesTitle: 'Our Core Values',
  values: [
    { title: 'Innovation', description: 'We continuously push the boundaries of hospitality technology with AI-driven insights, real-time analytics, and intelligent automation.' },
    { title: 'Security', description: 'Enterprise-grade data protection with SOC 2 compliance, AES-256 encryption, and rigorous access controls safeguard every transaction.' },
    { title: 'Dedicated Support', description: '24/7 customer success team with dedicated account managers ensuring seamless onboarding, training, and ongoing assistance.' },
    { title: 'Hospitality First', description: 'Built by hoteliers, for hoteliers. Every feature is designed with deep industry expertise to elevate the guest experience.' },
  ],
  story: {
    title: 'Our Story',
    paragraphs: [
      'HotelStack was founded in 2022 by a team of hospitality veterans and enterprise software engineers who recognized a fundamental gap in the market: hotels worldwide needed a modern, cloud-native platform that could scale from a single boutique property to a global chain -- with regional compliance, multi-currency support, and localized operations built in from day one.',
      'Today, HotelStack powers 2,500+ properties across 40+ countries, processing over 250,000 bookings and serving 65,000+ hospitality professionals daily. With regional offices in the Americas, Europe, Middle East, and Asia-Pacific, our commitment to innovation, reliability, and customer success drives every product decision we make.',
    ],
  },
};

const ar: AboutContent = {
  header: { title: 'عن هوتيل ستاك', description: 'تمكين مستقبل إدارة الضيافة.' },
  mission: { title: 'مهمتنا', description: 'تمكين الفنادق من جميع الأحجام بمنصة إدارة بمستوى المؤسسات تبسط العمليات، وتعظم الإيرادات، وتقدم تجارب ضيوف استثنائية -- كل ذلك من لوحة تحكم واحدة موحدة.' },
  vision: { title: 'رؤيتنا', description: 'أن نصبح المعيار العالمي في تكنولوجيا الضيافة السحابية، مما يتيح إدارة سلسة متعددة الممتلكات ووضع معايير جديدة للتميز التشغيلي واتخاذ القرارات المبنية على البيانات ورضا الضيوف.' },
  stats: [
    { label: 'الفنادق المسجلة', value: '+٢٬٥٠٠' },
    { label: 'الحجوزات المعالجة', value: '+٢٥٠ ألف' },
    { label: 'الدول المخدومة', value: '+٤٠' },
    { label: 'المستخدمون النشطون', value: '+٦٥٬٠٠٠' },
  ],
  valuesTitle: 'قيمنا الأساسية',
  values: [
    { title: 'الابتكار', description: 'ندفع باستمرار حدود تكنولوجيا الضيافة برؤى مدعومة بالذكاء الاصطناعي وتحليلات في الوقت الفعلي وأتمتة ذكية.' },
    { title: 'الأمان', description: 'حماية بيانات بمستوى المؤسسات مع شهادات أمنية وتشفير متقدم وضوابط وصول صارمة تحمي كل معاملة.' },
    { title: 'دعم مخصص', description: 'فريق نجاح عملاء يعمل على مدار الساعة مع مديري حسابات مخصصين يضمنون تأهيلاً وتدريباً ومساعدة مستمرة سلسة.' },
    { title: 'الضيافة أولاً', description: 'مبني بواسطة أصحاب الفنادق، لأصحاب الفنادق. كل ميزة مصممة بخبرة عميقة في الصناعة لرفع مستوى تجربة الضيف.' },
  ],
  story: {
    title: 'قصتنا',
    paragraphs: [
      'تأسست هوتيل ستاك في عام ٢٠٢٢ من قبل فريق من خبراء الضيافة ومهندسي البرمجيات المؤسسية الذين أدركوا فجوة أساسية في السوق: الفنادق في جميع أنحاء العالم تحتاج إلى منصة حديثة سحابية يمكنها التوسع من فندق بوتيك واحد إلى سلسلة عالمية.',
      'اليوم، تدعم هوتيل ستاك أكثر من ٢٬٥٠٠ عقار في أكثر من ٤٠ دولة، وتعالج أكثر من ٢٥٠٬٠٠٠ حجز وتخدم أكثر من ٦٥٬٠٠٠ محترف ضيافة يومياً.',
    ],
  },
};

const es: AboutContent = {
  header: { title: 'Sobre HotelStack', description: 'Impulsando el futuro de la gestion hotelera.' },
  mission: { title: 'Nuestra Mision', description: 'Empoderar hoteles de todos los tamanos con una plataforma de gestion empresarial que optimiza operaciones, maximiza ingresos y ofrece experiencias excepcionales para los huespedes.' },
  vision: { title: 'Nuestra Vision', description: 'Convertirnos en el estandar global en tecnologia hotelera basada en la nube, permitiendo la gestion fluida de multiples propiedades y estableciendo nuevos parametros de excelencia operativa.' },
  stats: [
    { label: 'Hoteles Registrados', value: '2,500+' },
    { label: 'Reservas Procesadas', value: '250K+' },
    { label: 'Paises Atendidos', value: '40+' },
    { label: 'Usuarios Activos', value: '65,000+' },
  ],
  valuesTitle: 'Nuestros Valores Fundamentales',
  values: [
    { title: 'Innovacion', description: 'Impulsamos continuamente los limites de la tecnologia hotelera con insights de IA, analiticas en tiempo real y automatizacion inteligente.' },
    { title: 'Seguridad', description: 'Proteccion de datos de nivel empresarial con cumplimiento SOC 2, cifrado AES-256 y controles de acceso rigurosos.' },
    { title: 'Soporte Dedicado', description: 'Equipo de exito del cliente 24/7 con gerentes de cuenta dedicados que garantizan una incorporacion y capacitacion sin problemas.' },
    { title: 'Hospitalidad Primero', description: 'Construido por hoteleros, para hoteleros. Cada funcion esta disenada con profunda experiencia en la industria.' },
  ],
  story: {
    title: 'Nuestra Historia',
    paragraphs: [
      'HotelStack fue fundada en 2022 por un equipo de veteranos de la hospitalidad e ingenieros de software empresarial que reconocieron una brecha fundamental en el mercado.',
      'Hoy, HotelStack impulsa mas de 2,500 propiedades en mas de 40 paises, procesando mas de 250,000 reservas y sirviendo a mas de 65,000 profesionales de la hospitalidad diariamente.',
    ],
  },
};

const fr: AboutContent = {
  header: { title: 'A Propos de HotelStack', description: 'Faconner l\'avenir de la gestion hoteliere.' },
  mission: { title: 'Notre Mission', description: 'Donner aux hotels de toute taille une plateforme de gestion d\'entreprise qui rationalise les operations, maximise les revenus et offre des experiences exceptionnelles aux clients.' },
  vision: { title: 'Notre Vision', description: 'Devenir la norme mondiale en technologie hoteliere cloud, permettant une gestion transparente multi-proprietes et etablissant de nouveaux reperes d\'excellence operationnelle.' },
  stats: [
    { label: 'Hotels Integres', value: '2,500+' },
    { label: 'Reservations Traitees', value: '250K+' },
    { label: 'Pays Desservis', value: '40+' },
    { label: 'Utilisateurs Actifs', value: '65,000+' },
  ],
  valuesTitle: 'Nos Valeurs Fondamentales',
  values: [
    { title: 'Innovation', description: 'Nous repoussons continuellement les limites de la technologie hoteliere avec des insights IA et une automatisation intelligente.' },
    { title: 'Securite', description: 'Protection des donnees de niveau entreprise avec conformite SOC 2, chiffrement AES-256 et controles d\'acces rigoureux.' },
    { title: 'Support Dedie', description: 'Equipe de succes client 24/7 avec des gestionnaires de comptes dedies assurant une integration et une formation sans faille.' },
    { title: 'L\'Hotellerie d\'Abord', description: 'Construit par des hoteliers, pour des hoteliers. Chaque fonctionnalite est concue avec une expertise approfondie de l\'industrie.' },
  ],
  story: {
    title: 'Notre Histoire',
    paragraphs: [
      'HotelStack a ete fondee en 2022 par une equipe de veterans de l\'hotellerie et d\'ingenieurs logiciels qui ont identifie un manque fondamental sur le marche.',
      'Aujourd\'hui, HotelStack alimente plus de 2 500 proprietes dans plus de 40 pays, traitant plus de 250 000 reservations et servant plus de 65 000 professionnels de l\'hotellerie quotidiennement.',
    ],
  },
};

const de: AboutContent = {
  header: { title: 'Über HotelStack', description: 'Die Zukunft des Hotelmanagements gestalten.' },
  mission: { title: 'Unsere Mission', description: 'Hotels jeder Größe mit einer Managementplattform auf Unternehmensniveau auszustatten, die Abläufe optimiert, Umsätze maximiert und außergewöhnliche Gästeerlebnisse bietet -- alles aus einem einheitlichen Dashboard.' },
  vision: { title: 'Unsere Vision', description: 'Der globale Standard in cloudbasierter Hoteltechnologie zu werden, nahtloses Multi-Property-Management zu ermöglichen und neue Maßstäbe für operative Exzellenz, datengestützte Entscheidungen und Gästezufriedenheit zu setzen.' },
  stats: [
    { label: 'Hotels an Bord', value: '2.500+' },
    { label: 'Verarbeitete Buchungen', value: '250K+' },
    { label: 'Bediente Länder', value: '40+' },
    { label: 'Aktive Nutzer', value: '65.000+' },
  ],
  valuesTitle: 'Unsere Kernwerte',
  values: [
    { title: 'Innovation', description: 'Wir erweitern kontinuierlich die Grenzen der Hoteltechnologie mit KI-gestützten Erkenntnissen, Echtzeit-Analysen und intelligenter Automatisierung.' },
    { title: 'Sicherheit', description: 'Datenschutz auf Unternehmensniveau mit SOC-2-Konformität, AES-256-Verschlüsselung und strengen Zugriffskontrollen schützt jede Transaktion.' },
    { title: 'Engagierter Support', description: '24/7-Kundenerfolgsteam mit dedizierten Account-Managern, die ein nahtloses Onboarding, Schulung und fortlaufende Betreuung gewährleisten.' },
    { title: 'Gastfreundschaft zuerst', description: 'Von Hoteliers für Hoteliers entwickelt. Jede Funktion ist mit profundem Branchenwissen gestaltet, um das Gästeerlebnis zu steigern.' },
  ],
  story: {
    title: 'Unsere Geschichte',
    paragraphs: [
      'HotelStack wurde 2022 von einem Team aus Hotellerie-Veteranen und Enterprise-Software-Ingenieuren gegründet, die eine grundlegende Marktlücke erkannten: Hotels weltweit benötigten eine moderne, cloud-native Plattform, die von einer einzelnen Boutique-Unterkunft bis hin zu einer globalen Hotelkette skalieren kann -- mit regionaler Compliance, Multi-Währungs-Unterstützung und lokalisierten Funktionen von Anfang an.',
      'Heute unterstützt HotelStack über 2.500 Properties in mehr als 40 Ländern, verarbeitet über 250.000 Buchungen und versorgt täglich mehr als 65.000 Gastgewerbeprofis. Mit Regionalbüros in Amerika, Europa, dem Nahen Osten und dem asiatisch-pazifischen Raum treiben unser Engagement für Innovation, Zuverlässigkeit und Kundenerfolg jede Produktentscheidung an, die wir treffen.',
    ],
  },
};

const th: AboutContent = {
  header: { title: 'เกี่ยวกับ HotelStack', description: 'เสริมพลังอนาคตของการจัดการการต้อนรับ' },
  mission: { title: 'พันธกิจของเรา', description: 'เสริมพลังโรงแรมทุกขนาดด้วยแพลตฟอร์มการจัดการระดับองค์กรที่ปรับปรุงการดำเนินงาน เพิ่มรายได้สูงสุด และมอบประสบการณ์ที่ยอดเยี่ยมแก่แขก' },
  vision: { title: 'วิสัยทัศน์ของเรา', description: 'เป็นมาตรฐานระดับโลกในเทคโนโลยีการต้อนรับบนคลาวด์ ช่วยให้การจัดการหลายทรัพย์สินเป็นไปอย่างราบรื่น' },
  stats: [
    { label: 'โรงแรมที่ลงทะเบียน', value: '2,500+' },
    { label: 'การจองที่ดำเนินการ', value: '250K+' },
    { label: 'ประเทศที่ให้บริการ', value: '40+' },
    { label: 'ผู้ใช้งานที่ใช้งานอยู่', value: '65,000+' },
  ],
  valuesTitle: 'ค่านิยมหลักของเรา',
  values: [
    { title: 'นวัตกรรม', description: 'เราผลักดันขอบเขตของเทคโนโลยีการต้อนรับอย่างต่อเนื่องด้วย AI และการวิเคราะห์แบบเรียลไทม์' },
    { title: 'ความปลอดภัย', description: 'การปกป้องข้อมูลระดับองค์กรด้วย SOC 2, การเข้ารหัส AES-256' },
    { title: 'การสนับสนุนเฉพาะ', description: 'ทีมความสำเร็จของลูกค้า 24/7 พร้อมผู้จัดการบัญชีเฉพาะ' },
    { title: 'การต้อนรับมาก่อน', description: 'สร้างโดยผู้ประกอบการโรงแรม เพื่อผู้ประกอบการโรงแรม' },
  ],
  story: { title: 'เรื่องราวของเรา', paragraphs: ['HotelStack ก่อตั้งในปี 2022 โดยทีมผู้เชี่ยวชาญด้านการต้อนรับและวิศวกรซอฟต์แวร์', 'ปัจจุบัน HotelStack รองรับกว่า 2,500 ทรัพย์สินใน 40+ ประเทศ'] },
};

const ja: AboutContent = {
  header: { title: 'HotelStackについて', description: 'ホスピタリティマネジメントの未来を支える。' },
  mission: { title: '私たちの使命', description: 'あらゆる規模のホテルにエンタープライズグレードの管理プラットフォームを提供し、運営を効率化し、収益を最大化し、優れたゲスト体験を提供します。' },
  vision: { title: '私たちのビジョン', description: 'クラウドベースのホスピタリティテクノロジーにおけるグローバルスタンダードとなり、シームレスなマルチプロパティ管理を実現します。' },
  stats: [
    { label: '導入ホテル', value: '2,500+' },
    { label: '処理予約数', value: '250K+' },
    { label: 'サービス提供国', value: '40+' },
    { label: 'アクティブユーザー', value: '65,000+' },
  ],
  valuesTitle: 'コアバリュー',
  values: [
    { title: 'イノベーション', description: 'AI駆動のインサイト、リアルタイム分析、インテリジェントオートメーションでホスピタリティテクノロジーの限界を押し広げます。' },
    { title: 'セキュリティ', description: 'SOC 2準拠、AES-256暗号化、厳格なアクセス制御によるエンタープライズグレードのデータ保護。' },
    { title: '専任サポート', description: '専任アカウントマネージャーによる24時間365日のカスタマーサクセスチーム。' },
    { title: 'ホスピタリティファースト', description: 'ホテリエが作った、ホテリエのためのシステム。業界の深い専門知識を活かした設計。' },
  ],
  story: { title: '私たちの物語', paragraphs: ['HotelStackは2022年にホスピタリティのベテランとエンタープライズソフトウェアエンジニアのチームによって設立されました。', '現在、HotelStackは40か国以上で2,500以上の施設を支え、毎日65,000人以上のホスピタリティプロフェッショナルにサービスを提供しています。'] },
};

const zh: AboutContent = {
  header: { title: '关于 HotelStack', description: '赋能酒店管理的未来。' },
  mission: { title: '我们的使命', description: '为各种规模的酒店提供企业级管理平台，简化运营、最大化收入并提供卓越的宾客体验。' },
  vision: { title: '我们的愿景', description: '成为基于云的酒店技术全球标准，实现无缝多物业管理，树立运营卓越的新标杆。' },
  stats: [
    { label: '入驻酒店', value: '2,500+' },
    { label: '处理预订', value: '250K+' },
    { label: '服务国家', value: '40+' },
    { label: '活跃用户', value: '65,000+' },
  ],
  valuesTitle: '核心价值观',
  values: [
    { title: '创新', description: '通过AI驱动的洞察、实时分析和智能自动化，不断突破酒店技术的边界。' },
    { title: '安全', description: 'SOC 2合规、AES-256加密和严格的访问控制提供企业级数据保护。' },
    { title: '专属支持', description: '24/7客户成功团队配备专属客户经理，确保无缝入驻和培训。' },
    { title: '酒店优先', description: '由酒店人为酒店人打造。每个功能都融入了深厚的行业专业知识。' },
  ],
  story: { title: '我们的故事', paragraphs: ['HotelStack由一支酒店业资深人士和企业软件工程师团队于2022年创立。', '如今，HotelStack为40多个国家的2,500多家酒店提供服务，每天处理超过250,000个预订。'] },
};

const hi: AboutContent = {
  header: { title: 'HotelStack के बारे में', description: 'आतिथ्य प्रबंधन के भविष्य को सशक्त बनाना।' },
  mission: { title: 'हमारा मिशन', description: 'हर आकार के होटलों को एक एंटरप्राइज़-ग्रेड प्रबंधन प्लेटफॉर्म से सशक्त बनाना जो संचालन को सुव्यवस्थित करे, राजस्व को अधिकतम करे, और उत्कृष्ट अतिथि अनुभव प्रदान करे -- सब एक एकीकृत डैशबोर्ड से।' },
  vision: { title: 'हमारी दृष्टि', description: 'क्लाउड-आधारित आतिथ्य प्रौद्योगिकी में वैश्विक मानक बनना, सहज मल्टी-प्रॉपर्टी प्रबंधन को सक्षम करना और परिचालन उत्कृष्टता, डेटा-संचालित निर्णय लेने और अतिथि संतुष्टि के लिए नए मानदंड स्थापित करना।' },
  stats: [
    { label: 'पंजीकृत होटल', value: '2,500+' },
    { label: 'प्रोसेस की गई बुकिंग', value: '250K+' },
    { label: 'सेवित देश', value: '40+' },
    { label: 'सक्रिय उपयोगकर्ता', value: '65,000+' },
  ],
  valuesTitle: 'हमारे मूल मूल्य',
  values: [
    { title: 'नवाचार', description: 'हम AI-संचालित अंतर्दृष्टि, रियल-टाइम एनालिटिक्स और बुद्धिमान स्वचालन के साथ आतिथ्य प्रौद्योगिकी की सीमाओं को लगातार आगे बढ़ाते हैं।' },
    { title: 'सुरक्षा', description: 'SOC 2 अनुपालन, AES-256 एन्क्रिप्शन और कड़े एक्सेस नियंत्रण के साथ एंटरप्राइज़-ग्रेड डेटा सुरक्षा।' },
    { title: 'समर्पित सहायता', description: 'समर्पित अकाउंट मैनेजरों के साथ 24/7 ग्राहक सफलता टीम।' },
    { title: 'आतिथ्य पहले', description: 'होटल मालिकों द्वारा, होटल मालिकों के लिए बनाया गया। हर सुविधा गहन उद्योग विशेषज्ञता के साथ डिज़ाइन की गई है।' },
  ],
  story: {
    title: 'हमारी कहानी',
    paragraphs: [
      'HotelStack की स्थापना 2022 में आतिथ्य विशेषज्ञों और एंटरप्राइज़ सॉफ्टवेयर इंजीनियरों की एक टीम द्वारा की गई थी जिन्होंने बाजार में एक मूलभूत अंतर को पहचाना: दुनिया भर के होटलों को एक आधुनिक, क्लाउड-नेटिव प्लेटफॉर्म की आवश्यकता थी।',
      'आज, HotelStack 40+ देशों में 2,500+ संपत्तियों को शक्ति प्रदान करता है, 250,000 से अधिक बुकिंग प्रोसेस करता है और दैनिक 65,000+ आतिथ्य पेशेवरों की सेवा करता है।',
    ],
  },
};

export const ABOUT_CONTENT: AboutContentMap = { en, ar, es, fr, de, th, ja, zh, hi };
